import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import { ListShiftResponse, ShiftResponse } from '../Types/Responses/ShiftResponses'
import { CreateShift } from '../Types/Requests/ShiftRequests'

/**
 * Turnos da escala (`Shift` = turno, NÃO plantão — ver rule domain-shift-vs-station).
 *
 * DÉBITO ESTRUTURAL CONHECIDO: cópia byte a byte de `shiftService.ts` (mesmo
 * prefixo `/shift`, mesma classe). Aquele é o registrado no singleton
 * (`Network.shift`); este é instanciado direto por
 * `createStation/components/form/doctor.infos.form.vue`. A unificação exige mexer
 * nesses consumidores e fica fora do escopo deste PR (só mensagens de erro).
 */
export default class ShiftService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/shift'
  }

  async create(payload: CreateShift): Promise<ShiftResponse> {
    try {
      const { data } = await request.post<ShiftResponse>(`${this.getCompletePath()}`, payload)
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível criar o turno.')
    }
  }

  async get(): Promise<ShiftResponse[]> {
    try {
      const { data } = await request.get<{ data: ShiftResponse[] }>(this.getCompletePath())
      return data.data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os turnos.')
    }
  }

  async list(): Promise<ListShiftResponse> {
    try {
      const { data } = await request.get<ListShiftResponse>(`${this.getCompletePath()}`)
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os turnos.')
    }
  }

  async remove(shiftId: number): Promise<void> {
    try {
      await request.delete(`${this.getCompletePath()}/${shiftId}`)
    } catch (e) {
      throw this.handleError(e, 'Não foi possível excluir o turno.')
    }
  }

  async update(shiftId: number, payload: CreateShift): Promise<ShiftResponse> {
    try {
      const { data } = (await request.patch(`${this.getCompletePath()}/${shiftId}`, payload)) as {
        data: ShiftResponse
      }
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível salvar as alterações do turno.')
    }
  }

  async getByScheduleId(scheduleId: number): Promise<ListShiftResponse> {
    try {
      const { data } = await request.get<ListShiftResponse>(
        `${this.getCompletePath()}/schedule/${scheduleId}`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os turnos da escala.')
    }
  }

  async getById(shiftId: number): Promise<ShiftResponse> {
    try {
      const { data } = await request.get<ShiftResponse>(`${this.getCompletePath()}/${shiftId}`)
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os dados do turno.')
    }
  }

  async updateBulk(scheduleId: number, payload: ShiftResponse[]): Promise<void> {
    try {
      await request.patch(`${this.getCompletePath()}/bulk/schedule/${scheduleId}`, payload)
    } catch (e) {
      throw this.handleError(e, 'Não foi possível salvar os turnos da escala.')
    }
  }
}
